import React, { useEffect, useState } from 'react';
import { passwordChecks, passwordOk } from '@/utils/passwordRules';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
let resetTokenInMemory = '';

function takeResetToken() {
  const params = new URLSearchParams(window.location.hash.slice(1));
  const token = params.get('token');
  if (token) {
    resetTokenInMemory = token;
    window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`);
  }
  return resetTokenInMemory;
}

export function ResetPasswordPage() {
  const [token] = useState(takeResetToken);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const appBase = window.location.pathname.replace(/reset-password$/, '') || '/';

  useEffect(() => {
    const prior = document.referrerPolicy;
    document.referrerPolicy = 'no-referrer';
    return () => { document.referrerPolicy = prior; };
  }, []);

  const submit = async () => {
    setError(null);
    if (!token) { setError('This password reset link is invalid or expired.'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (!passwordOk(password)) { setError('Your password needs 8+ characters with a capital letter, a lowercase letter, a number and a symbol.'); return; }
    if (new TextEncoder().encode(password).length > 72) {
      setError('Your password must be no more than 72 UTF-8 bytes.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(response.status === 429
          ? 'Too many attempts. Please wait a minute and try again.'
          : data.detail || 'This password reset link is invalid or expired.');
        return;
      }
      setSuccess(true);
      resetTokenInMemory = '';
      setPassword('');
      setConfirmPassword('');
    } catch (e) {
      setError("Can't reach the server. Check your Wi-Fi and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="fixed inset-0 flex items-center justify-center" style={{ background: 'var(--bg)' }}>
      <section style={{ width: 400, background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border)', overflow: 'hidden' }}>
        <header className="flex items-center gap-2 px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <img src="/Pizzul_Hero.png" alt="" style={{ width: 28, height: 28, borderRadius: '50%', border: '2px solid var(--green)', objectFit: 'cover' }} />
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>Reset your password</span>
        </header>
        <div className="px-5 py-5 flex flex-col gap-3">
          {success ? (
            <>
              <p data-testid="reset-success" style={{ color: 'var(--text)', fontSize: 13 }}>Your password has been changed. You can now sign in.</p>
              <a href={appBase} style={{ color: 'var(--green)', fontSize: 13, textAlign: 'center' }}>Return to Beat Arranger</a>
            </>
          ) : (
            <>
              <input data-testid="reset-password" type="password" autoComplete="new-password" placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} className="outline-none" style={{ height: 40, borderRadius: 8, padding: '0 14px', background: 'var(--bg-elevated)', color: 'var(--text)', border: '1px solid var(--border)', fontSize: 13 }} />
              <ul data-testid="reset-password-checklist" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 3 }}>
                {passwordChecks(password).map((rule) => (
                  <li key={rule.id} style={{ fontSize: 12, color: rule.ok ? '#00D4AA' : '#777' }}>{rule.ok ? '✓' : '○'} {rule.label}</li>
                ))}
              </ul>
              <input data-testid="reset-password-confirm" type="password" autoComplete="new-password" placeholder="Confirm new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} className="outline-none" style={{ height: 40, borderRadius: 8, padding: '0 14px', background: 'var(--bg-elevated)', color: 'var(--text)', border: '1px solid var(--border)', fontSize: 13 }} />
              {error && <span data-testid="reset-error" style={{ fontSize: 12, color: 'var(--pink)', paddingLeft: 2 }}>{error}</span>}
              <button data-testid="reset-submit" onClick={submit} disabled={loading} style={{ height: 42, borderRadius: 22, fontSize: 14, fontWeight: 600, background: loading ? 'var(--bg-elevated)' : 'var(--green)', color: loading ? 'var(--text-dim)' : 'var(--bg)', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                {loading && <Loader2 size={15} className="animate-spin" />} Change password
              </button>
            </>
          )}
        </div>
      </section>
    </main>
  );
}

export default ResetPasswordPage;
